import { database, DB_PREFIX, cacheGet, cacheSet, CACHE_TTL, invalidateCache, isDevelopment } from "./config";
import {
  ref, push, serverTimestamp, set,
  onValue, get, query, orderByChild, equalTo, remove
} from "firebase/database";
import { updateBusinessStats } from "./business";


export const getAllReviews = async () => {
  const cacheKey = 'reviews:all';
  const cached = cacheGet(cacheKey);
  if (cached !== undefined) return cached;
  if (!database) {
    console.warn("Firebase not initialized");
    return [];
  }
  try {
    const reviewsRef = ref(database, `${DB_PREFIX}reviews`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(reviewsRef, resolve, reject, { onlyOnce: true });
    });
    const reviews = [];
    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot) => {
        reviews.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
    }
    cacheSet(cacheKey, reviews, CACHE_TTL.reviews);
    return reviews;
  } catch (error) {
    console.error("Error getting all reviews:", error);
    return [];
  }
};

export const addReview = async (reviewData) => {
  if (!database) {
    console.warn("Firebase not initialized - review not saved");
    return null;
  }

  try {
    const reviewsRef = ref(database, `${DB_PREFIX}reviews`);
    const newReviewRef = push(reviewsRef);
    await set(newReviewRef, {
      ...reviewData,
      createdAt: serverTimestamp()
    });

    // Update business stats
    await updateBusinessStats(reviewData.businessId);

    console.log("Review saved to Firebase successfully");
    invalidateCache(`reviews:business:${reviewData.businessId}`);
    return newReviewRef.key;

  } catch (error) {
    console.error("Error saving review to Firebase:", error);
    throw error;
  }
};

export const updateReview = async (reviewId, reviewData) => {
  if (!database) {
    console.warn("Firebase not initialized - review not updated");
    return null;
  }

  try {
    const reviewRef = ref(database, `${DB_PREFIX}reviews/${reviewId}`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(reviewRef, resolve, reject, { onlyOnce: true });
    });

    if (!snapshot.exists()) {
      console.error("Review not found");
      return null;
    }

    const existingReview = snapshot.val();

    // Update the review while preserving createdAt and other metadata
    await set(reviewRef, {
      ...existingReview,
      ...reviewData,
      updatedAt: serverTimestamp()
    });

    // Update business stats in case rating changed
    await updateBusinessStats(reviewData.businessId || existingReview.businessId);

    console.log("Review updated successfully");
    invalidateCache(`reviews:business:${reviewData.businessId}`);
    return reviewId;
  } catch (error) {
    console.error("Error updating review:", error);
    throw error;
  }
};
export const getUserReviews = async (userEmail) => {
  const cacheKey = `reviews:user:${userEmail.toLowerCase()}`;
  const cached = cacheGet(cacheKey);
  if (cached !== undefined) return cached;

  if (!database) {
    console.warn("Firebase not initialized");
    return [];
  }

  try {
    const reviewsRef = ref(database, `${DB_PREFIX}reviews`);
    const reviewsQuery = query(
      reviewsRef,
      orderByChild("userEmail"),
      equalTo(userEmail)
    );

    const snapshot = await get(reviewsQuery);

    const reviews = [];
    snapshot.forEach((childSnapshot) => {
      reviews.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    cacheSet(cacheKey, reviews, CACHE_TTL.reviews);
    return reviews;
  } catch (error) {
    console.error("Error getting user reviews:", error);
    return [];
  }
};

export const getBusinessReviews = async (businessId) => {
  const cacheKey = `reviews:business:${businessId}`;
  const cached = cacheGet(cacheKey);
  if (cached !== undefined) return cached;

  if (!database) {
    console.warn("Firebase not initialized");
    return [];
  }

  try {
    const reviewsRef = ref(database, `${DB_PREFIX}reviews`);
    const reviewsQuery = query(
      reviewsRef,
      orderByChild("businessId"),
      equalTo(businessId)
    );

    const snapshot = await get(reviewsQuery);

    const reviews = [];
    snapshot.forEach((childSnapshot) => {
      reviews.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    reviews.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    cacheSet(cacheKey, reviews, CACHE_TTL.reviews);
    return reviews;
  } catch (error) {
    console.error("Error getting business reviews:", error);
    return [];
  }
};

// Get all comments from the database
export const getAllComments = async () => {
  const cacheKey = 'comments:all';
  const cached = cacheGet(cacheKey);
  if (cached !== undefined) return cached;
  if (!database) {
    console.warn("Firebase not initialized");
    return [];
  }
  try {
    const commentsRef = ref(database, `${DB_PREFIX}comments`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(commentsRef, resolve, reject, { onlyOnce: true });
    });
    const allComments = [];
    if (snapshot.exists()) {
      snapshot.forEach((reviewSnapshot) => {
        const reviewId = reviewSnapshot.key;
        reviewSnapshot.forEach((commentSnapshot) => {
          allComments.push({
            id: commentSnapshot.key,
            reviewId: reviewId,
            ...commentSnapshot.val()
          });
        });
      });
    }
    cacheSet(cacheKey, allComments, CACHE_TTL.reviews);
    return allComments;
  } catch (error) {
    console.error("Error getting all comments:", error);
    return [];
  }
};

// Anonymize all reviews and comments - removes username fields from existing data
export const anonymizeAllReviewsAndComments = async () => {
  if (!database) {
    console.warn("Firebase not initialized");
    return { success: false, message: "Firebase not initialized" };
  }

  try {
    console.log("Starting anonymization of reviews and comments...");
    let reviewsUpdated = 0;
    let commentsUpdated = 0;
    let businessesUpdated = 0;

    // 1. Anonymize reviews
    const reviewsRef = ref(database, `${DB_PREFIX}reviews`);
    const reviewsSnapshot = await new Promise((resolve, reject) => {
      onValue(reviewsRef, resolve, reject, { onlyOnce: true });
    });

    const reviewPromises = [];
    reviewsSnapshot.forEach((childSnapshot) => {
      const review = childSnapshot.val();
      if (review.username) {
        const reviewRef = ref(database, `${DB_PREFIX}reviews/${childSnapshot.key}`);
        const { username, ...reviewWithoutUsername } = review;
        reviewPromises.push(set(reviewRef, reviewWithoutUsername));
        reviewsUpdated++;
      }
    });
    await Promise.all(reviewPromises);
    console.log(`Anonymized ${reviewsUpdated} reviews`);

    // 2. Anonymize comments
    const commentsRef = ref(database, `${DB_PREFIX}comments`);
    const commentsSnapshot = await new Promise((resolve, reject) => {
      onValue(commentsRef, resolve, reject, { onlyOnce: true });
    });

    const commentPromises = [];
    commentsSnapshot.forEach((reviewCommentsSnapshot) => {
      reviewCommentsSnapshot.forEach((commentSnapshot) => {
        const comment = commentSnapshot.val();
        if (comment.username) {
          const commentRef = ref(database, `${DB_PREFIX}comments/${reviewCommentsSnapshot.key}/${commentSnapshot.key}`);
          const { username, ...commentWithoutUsername } = comment;
          commentPromises.push(set(commentRef, commentWithoutUsername));
          commentsUpdated++;
        }
      });
    });
    await Promise.all(commentPromises);
    console.log(`Anonymized ${commentsUpdated} comments`);

    // 3. Remove addedByUsername from businesses
    const businessesRef = ref(database, `${DB_PREFIX}businesses`);
    const businessesSnapshot = await new Promise((resolve, reject) => {
      onValue(businessesRef, resolve, reject, { onlyOnce: true });
    });

    const businessPromises = [];
    businessesSnapshot.forEach((childSnapshot) => {
      const business = childSnapshot.val();
      if (business.addedByUsername || business.addedBy) {
        const businessRef = ref(database, `${DB_PREFIX}businesses/${childSnapshot.key}`);
        const { addedByUsername, addedBy, ...businessWithoutAdder } = business;
        businessPromises.push(set(businessRef, businessWithoutAdder));
        businessesUpdated++;
      }
    });
    await Promise.all(businessPromises);
    console.log(`Anonymized ${businessesUpdated} businesses`);

    const result = {
      success: true,
      message: `Anonymization complete. Reviews: ${reviewsUpdated}, Comments: ${commentsUpdated}, Businesses: ${businessesUpdated}`,
      reviewsUpdated,
      commentsUpdated,
      businessesUpdated
    };

    console.log("Anonymization complete:", result);
    return result;
  } catch (error) {
    console.error("Error anonymizing data:", error);
    return { success: false, message: error.message };
  }
};

// Remove job_title field from all user profiles
export const removeJobTitleFromAllProfiles = async () => {
  if (!database) {
    console.warn("Firebase not initialized");
    return { success: false, message: "Firebase not initialized" };
  }

  try {
    console.log("Starting removal of job_title from all user profiles...");
    let updatedCount = 0;

    // Check both dev and production paths
    const pathsToCheck = [
      `${DB_PREFIX}users`,
      isDevelopment ? 'users' : 'dev/users'
    ];

    for (const path of pathsToCheck) {
      console.log(`Checking path: ${path}`);
      const usersRef = ref(database, path);
      const snapshot = await new Promise((resolve, reject) => {
        onValue(usersRef, resolve, reject, { onlyOnce: true });
      });

      const updatePromises = [];
      snapshot.forEach((childSnapshot) => {
        const userData = childSnapshot.val();
        if (Object.prototype.hasOwnProperty.call(userData, 'job_title')) {
          const userRef = ref(database, `${path}/${childSnapshot.key}`);
          // Remove job_title by setting it to null
          const { job_title, ...userWithoutJobTitle } = userData;
          updatePromises.push(set(userRef, userWithoutJobTitle));
          updatedCount++;
          console.log(`Will remove job_title from user: ${userData.email || userData.username || childSnapshot.key}`);
        }
      });

      await Promise.all(updatePromises);
    }

    const result = {
      success: true,
      message: `Removed job_title from ${updatedCount} user profile(s)`,
      updatedCount
    };

    console.log("Job title removal complete:", result);
    return result;

  } catch (error) {
    console.error("Error removing job_title:", error);
    return { success: false, message: error.message };
  }
};

export const deleteReview = async (reviewId, businessId) => {
  if (!database) {
    console.warn("Firebase not initialized - review not deleted");
    return false;
  }

  try {
    const reviewRef = ref(database, `${DB_PREFIX}reviews/${reviewId}`);
    await remove(reviewRef);

    // Update business stats after deletion
    if (businessId) {
      await updateBusinessStats(businessId);

      // Check if there are any reviews left for this business
      const remainingReviews = await getBusinessReviews(businessId);

      // If no reviews left, delete the business
      if (remainingReviews.length === 0) {
        const businessRef = ref(database, `${DB_PREFIX}businesses/${businessId}`);
        await remove(businessRef);
        console.log("Business deleted as it has no remaining reviews");
      }
    }

    console.log("Review deleted successfully");
    invalidateCache(`reviews:business:${businessId}`);
    return true;
  } catch (error) {
    console.error("Error deleting review:", error);
    throw error;
  }
};

export const toggleThumbsUp = async (reviewId, userEmail) => {
  if (!database) {
    console.warn("Firebase not initialized - thumbs up not saved");
    return null;
  }

  try {
    const reviewRef = ref(database, `${DB_PREFIX}reviews/${reviewId}`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(reviewRef, resolve, reject, { onlyOnce: true });
    });

    if (!snapshot.exists()) {
      console.error("Review not found");
      return null;
    }

    const reviewData = snapshot.val();
    const thumbsUpUsers = reviewData.thumbsUpUsers || [];

    let newThumbsUpUsers;
    if (thumbsUpUsers.includes(userEmail)) {
      // Remove thumbs up
      newThumbsUpUsers = thumbsUpUsers.filter(email => email !== userEmail);
    } else {
      // Add thumbs up
      newThumbsUpUsers = [...thumbsUpUsers, userEmail];
    }

    await set(reviewRef, {
      ...reviewData,
      thumbsUpUsers: newThumbsUpUsers,
      thumbsUpCount: newThumbsUpUsers.length
    });

    console.log("Thumbs up toggled successfully");
    invalidateCache(`reviews:business:${reviewData.businessId}`);
    return newThumbsUpUsers.length;
  } catch (error) {
    console.error("Error toggling thumbs up:", error);
    throw error;
  }
};

export const addComment = async (reviewId, commentData) => {
  if (!database) {
    console.warn("Firebase not initialized - comment not saved");
    return null;
  }

  try {
    const commentsRef = ref(database, `${DB_PREFIX}comments/${reviewId}`);
    const newCommentRef = push(commentsRef);
    await set(newCommentRef, {
      ...commentData,
      createdAt: serverTimestamp()
    });

    console.log("Comment saved successfully");
    invalidateCache(`comments:review:${reviewId}`);
    return newCommentRef.key;
  } catch (error) {
    console.error("Error saving comment:", error);
    throw error;
  }
};

export const getReviewComments = async (reviewId) => {
  const cacheKey = `comments:review:${reviewId}`;
  const cached = cacheGet(cacheKey);
  if (cached !== undefined) return cached;
  if (!database) {
    console.warn("Firebase not initialized");
    return [];
  }

  try {
    const commentsRef = ref(database, `${DB_PREFIX}comments/${reviewId}`);
    const snapshot = await new Promise((resolve, reject) => {
      onValue(commentsRef, resolve, reject, { onlyOnce: true });
    });

    const comments = [];
    snapshot.forEach((childSnapshot) => {
      comments.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by creation date (oldest first for chronological order)
    comments.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    cacheSet(cacheKey, comments, CACHE_TTL.reviews);
    return comments;
  } catch (error) {
    console.error("Error getting review comments:", error);
    return [];
  }
};

export const deleteComment = async (reviewId, commentId) => {
  if (!database) {
    console.warn("Firebase not initialized - comment not deleted");
    return false;
  }

  try {
    const commentRef = ref(database, `${DB_PREFIX}comments/${reviewId}/${commentId}`);
    await remove(commentRef);
    console.log("Comment deleted successfully");
    invalidateCache(`comments:review:${reviewId}`);
    return true;
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
};

// Expose anonymization function globally for console access
if (typeof window !== 'undefined') {
  window.anonymizeAllReviews = anonymizeAllReviewsAndComments;
  window.removeJobTitleFromAllProfiles = removeJobTitleFromAllProfiles;
}